import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, inject } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';

import { Fai, Plan, ProductFactory, QUOTE_CONTEXTS } from '@bytel/bytel-sales';
import { LimitedCommercialSeries } from '@bytel/checkout';

import { MainCartService } from '@services/main-cart.service';

import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

@Injectable({
    providedIn: 'root',
})
export class RecallService {
    @Inject(DOCUMENT) private dom: Document = inject(DOCUMENT);
    private route: ActivatedRoute = inject(ActivatedRoute);
    private router: Router = inject(Router);
    private cartService: MainCartService = inject(MainCartService);

    private readonly BASE_RECALL_URL: string = 'https://www.bouyguestelecom.fr/rappelez-moi';
    private readonly DEFAULT_RECALL_PARAM: string = 'tlvctcmobile1';
    private readonly MARKET_LINE = {
        pro: 'PRO',
        gp: 'GP',
    };
    private readonly PRODUCT_TYPE = {
        sowo: 'SOWO',
        sensation: 'FAM',
        simo: 'SIMO',
        fai: 'FAI',
    };

    public initRouteHandler(): Observable<void> {
        return this.router.events.pipe(
            filter((event) => event instanceof NavigationEnd),
            map(() => {
                this.setMetaRecall(this.getRecallParam(this.route.firstChild));
            }),
        );
    }

    public setMetaRecall(recallParam: string): void {
        this.setMetaUrl(this.getRecallUrl(recallParam));
        this.setMetaContext();
    }

    private getRecallUrl(recallParam: string): string {
        return `${this.BASE_RECALL_URL}?callback=${recallParam}`;
    }

    private getRecallParam(activatedRoute: ActivatedRoute | null): string {
        while (activatedRoute) {
            if (activatedRoute.firstChild) {
                activatedRoute = activatedRoute.firstChild;
            } else if (activatedRoute.snapshot.data?.recallParam) {
                return activatedRoute.snapshot.data.recallParam;
            } else {
                return this.DEFAULT_RECALL_PARAM;
            }
        }
    }

    private setMetaUrl(url: string): void {
        const recall = this.dom.head.querySelector('meta[name=recall]');
        recall.setAttribute('content', url);
    }

    private setMetaContext(): void {
        let quoteContext: string;
        if (
            this.cartService.getQuoteFai()?.getPrincipalProduct<Fai>('Fai').data.commercial_series ===
            LimitedCommercialSeries.B_AND_YOU
        ) {
            const recallUrl = this.dom.head.querySelector('meta[name=recall]');
            recallUrl.setAttribute('content', '');
        }

        const quote = this.cartService.getQuote();
        if (quote) {
            quoteContext = quote.isRenewal() ? QUOTE_CONTEXTS.RENEW : QUOTE_CONTEXTS.ACQUISITION;
            quoteContext += this.cartService.cartModel.isPro ? `-${this.MARKET_LINE.pro}` : `-${this.MARKET_LINE.gp}`;
            quoteContext += this.getProductType();
            const recall = this.dom.head.querySelector('meta[name=recall-context]');
            recall.setAttribute('content', quoteContext);
        }
    }

    private getProductType(): string {
        const quote = this.cartService.getQuote();
        const plan = quote?.getPrincipalProduct<Plan>('Plan');
        let productType: string;

        switch (true) {
            case ProductFactory.Is(plan, 'Sensation'):
                productType = `-${this.PRODUCT_TYPE.sensation}`;
                break;

            case ProductFactory.Is(plan, 'Sowo'):
                productType = `-${this.PRODUCT_TYPE.sowo}`;
                break;

            case ProductFactory.Is(plan, 'Simo'):
                productType = `-${this.PRODUCT_TYPE.simo}`;
                break;

            case ProductFactory.Is(plan, 'Fai'):
                productType = `-${this.PRODUCT_TYPE.fai}`;
                break;

            default:
                productType = '';
        }

        return productType;
    }
}
